import { useCallback, useEffect, useMemo, useState } from 'react'

import { ErrorBoundary } from '@scoir/error-reporter'
import { AVAILABLE_REDIRECTS, getRedirectURLToEquivalentPage, setSignedInApp } from '@scoir/redirects'
import { get, has } from 'lodash'
import { connect } from 'react-redux'

import { getUnauthedFallbackUrl } from 'common/api'
import { hasBearerToken } from 'common/bearer'
import window from 'common/window'
import ExternalRedirect from 'components/ExternalRedirect'
import TrackRememberedUrl from 'components/TrackRememberedUrl'
import { fetchCurrentAppCycle } from 'store/application-cycle'
import { actions as collegeActions } from 'store/college'
import { fetchCurrentSeniorYear } from 'store/current-senior-year'
import { actions as featuresToggleActions } from 'store/feature-toggle'
import { actions as metricsActions } from 'store/metrics'
import { actions as userActions } from 'store/user'

type IUserStartupProps = {
  userId?: string
  userScid?: number
  fetchFeatureToggles: () => Promise<void>
  fetchUser: () => Promise<void>
  fetchCollege: (scid: number) => Promise<void>
  fetchCurrentAppCycle: () => Promise<void>
  fetchCurrentSeniorYear: () => Promise<void>
  fetchMetrics: (scid: number) => Promise<void>
  children: React.ReactNode
}

const getRedirectURL = (): string => {
  const equivalentPageURL = getRedirectURLToEquivalentPage()
  return equivalentPageURL || `${window.location.origin}${getUnauthedFallbackUrl()}`
}

const UserStartup = ({
  userId,
  userScid,
  fetchUser,
  fetchCollege,
  fetchFeatureToggles,
  fetchCurrentAppCycle,
  fetchCurrentSeniorYear,
  fetchMetrics,
  children,
}: IUserStartupProps) => {
  const [settled, setSettled] = useState({
    featureToggles: false,
    user: false,
    college: false,
    appCycle: false,
    seniorYear: false,
    metrics: false,
  })
  const markSettled = useCallback((key: keyof typeof settled, value: boolean) => {
    setSettled((prev) => ({ ...prev, [key]: value }))
  }, [])

  const hasToken = useMemo(() => hasBearerToken(), [])
  const isAuthed = !!userId

  useEffect(() => {
    fetchFeatureToggles().finally(() => markSettled('featureToggles', true))
  }, [fetchFeatureToggles, markSettled])

  const canFetchUser = hasToken && settled.featureToggles
  useEffect(() => {
    if (!canFetchUser) return

    fetchUser().finally(() => markSettled('user', true))
  }, [canFetchUser, fetchUser, markSettled])

  const canFetchSchool = isAuthed
  useEffect(() => {
    if (!canFetchSchool) return

    fetchCollege(userScid as number).finally(() => markSettled('college', true))
  }, [canFetchSchool, userScid, fetchCollege, markSettled])

  useEffect(() => {
    if (!isAuthed) return

    fetchCurrentAppCycle().finally(() => markSettled('appCycle', true))
    fetchCurrentSeniorYear().finally(() => markSettled('seniorYear', true))
  }, [isAuthed, fetchCurrentAppCycle, fetchCurrentSeniorYear, markSettled])

  const canFetchMetrics = isAuthed && settled.user && settled.college && settled.featureToggles
  useEffect(() => {
    if (!canFetchMetrics) return

    fetchMetrics(userScid as number).finally(() => markSettled('metrics', true))
  }, [canFetchMetrics, userScid, fetchMetrics, markSettled])

  useEffect(() => {
    if (!isAuthed) return
    setSignedInApp('admissions', AVAILABLE_REDIRECTS.COLLEGE_PROFILE)
  }, [isAuthed])

  const initialized = Object.values(settled).every((v) => v)
  const isNotAuthed = !hasToken

  return initialized ? (
    <ErrorBoundary>{children}</ErrorBoundary>
  ) : isNotAuthed ? (
    <>
      <ExternalRedirect to={getRedirectURL()} />
      <TrackRememberedUrl />
    </>
  ) : null
}

export default connect(
  (store: any) => {
    const user = store.user.record
    return {
      userId: get(user, 'Id'),
      userScid: get(user, 'Scid'),
      hasCollege: has(store.college, 'record'),
    }
  },
  {
    fetchFeatureToggles: featuresToggleActions.fetchFeatureToggles,
    fetchUser: userActions.fetchUser,
    fetchCollege: collegeActions.fetchCollege,
    fetchCurrentAppCycle,
    fetchCurrentSeniorYear,
    fetchMetrics: metricsActions.fetchMetrics,
  },
)(UserStartup as React.FunctionComponent)
